import { isPublicAnalyticsRoute } from "@/src/features/posthog-analytics/identity";

/**
 * CIP fork: one PostHog kill switch per Studio feature area
 * (README.md). Each flag defaults ON: a page hides only when PostHog
 * explicitly returns `false` for its area, so a PostHog outage, an
 * ad-blocker or a flag nobody created never hides anything.
 *
 * Order matters: the first matching prefix wins. `pageAreas.servertest.ts`
 * checks every page under `src/pages` resolves to exactly one area, so a new
 * page can't ship without a switch.
 */
const AREA_PREFIXES = [
  [
    "page-elicitations",
    [
      "/project/[projectId]/elicitations",
      "/organization/[organizationId]/elicitations",
    ],
  ],
  ["page-assemblies", ["/project/[projectId]/assemblies"]],
  ["page-blueprints", ["/project/[projectId]/blueprints"]],
  ["page-rubrics", ["/project/[projectId]/rubrics"]],
  ["page-participants", ["/project/[projectId]/participants"]],
  ["page-datasets", ["/project/[projectId]/datasets"]],
  ["page-experiments", ["/project/[projectId]/experiments"]],
  ["page-evals", ["/project/[projectId]/evals"]],
  ["page-scores", ["/project/[projectId]/scores"]],
  ["page-annotation-queues", ["/project/[projectId]/annotation-queues"]],
  [
    "page-tracing",
    [
      "/project/[projectId]/traces",
      "/project/[projectId]/observations",
      "/project/[projectId]/sessions",
      "/project/[projectId]/users",
      "/trace/[traceId]",
    ],
  ],
  ["page-prompts", ["/project/[projectId]/prompts"]],
  ["page-playground", ["/project/[projectId]/playground"]],
  [
    "page-dashboards",
    ["/project/[projectId]/dashboards", "/project/[projectId]/widgets"],
  ],
  ["page-models", ["/project/[projectId]/models"]],
  ["page-alerts", ["/project/[projectId]/alerts"]],
  ["page-automations", ["/project/[projectId]/automations"]],
  [
    "page-settings",
    [
      "/project/[projectId]/settings",
      "/organization/[organizationId]/settings",
      "/account/settings",
      // CIP fork (see FORK.md): the MCP consent page (cip-mcp-access) rides
      // the settings switch.
      "/account/mcp",
    ],
  ],
  [
    "page-setup",
    [
      "/project/[projectId]/setup",
      "/organization/[organizationId]/setup",
      "/setup",
      "/onboarding",
    ],
  ],
  ["page-system", ["/background-migrations", "/v4-migration"]],
  // Landing pages match exactly, so an unmapped sub-page fails the test
  // instead of silently sharing the home switch.
  [
    "page-home",
    [
      "=/project/[projectId]",
      "/project/~",
      "=/organization/[organizationId]",
      "=/",
    ],
  ],
] as const satisfies ReadonlyArray<readonly [string, readonly string[]]>;

export type PageAreaFlag = (typeof AREA_PREFIXES)[number][0];

export const PAGE_AREA_FLAGS: readonly PageAreaFlag[] = AREA_PREFIXES.map(
  ([flag]) => flag,
);

// A leading "=" matches the route exactly; otherwise the route and every
// route under it match.
const matches = (pathname: string, prefix: string) =>
  prefix.startsWith("=")
    ? pathname === prefix.slice(1)
    : pathname === prefix || pathname.startsWith(`${prefix}/`);

/**
 * The kill switch for a route pattern (`router.pathname`), or `null` for
 * pages that are never gated: public forms, partner views and sign-in.
 */
export const pageAreaFlagFor = (pathname: string): PageAreaFlag | null => {
  if (isPublicAnalyticsRoute(pathname)) return null;
  for (const [flag, prefixes] of AREA_PREFIXES) {
    if (prefixes.some((prefix) => matches(pathname, prefix))) return flag;
  }
  return null;
};
